import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { History, Plus, TrendingUp, TrendingDown, ArrowRightLeft, Filter } from 'lucide-react';

export default function StockMovementsPage() {
  const [movements, setMovements] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movementDialog, setMovementDialog] = useState(false);
  const [transferDialog, setTransferDialog] = useState(false);
  const [filterStore, setFilterStore] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const [movementForm, setMovementForm] = useState({ 
    store_id: '', item_id: '', movement_type: 'stock_out', quantity: 0, unit_cost: 0, notes: '' 
  });
  const [transferForm, setTransferForm] = useState({ 
    from_store_id: '', to_store_id: '', items: [{ item_id: '', quantity: 0 }] 
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [movementsRes, storesRes, itemsRes] = await Promise.all([
        api.get('/stock-movements'),
        api.get('/stores'),
        api.get('/stock-items')
      ]);
      setMovements(movementsRes.data);
      setStores(storesRes.data);
      setItems(itemsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateMovement = async () => {
    try {
      await api.post('/stock-movements', movementForm);
      toast.success('Stock movement recorded');
      setMovementDialog(false);
      setMovementForm({ store_id: '', item_id: '', movement_type: 'stock_out', quantity: 0, unit_cost: 0, notes: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record movement');
    }
  };

  const handleCreateTransfer = async () => {
    try {
      await api.post('/stock-transfers', transferForm);
      toast.success('Stock transferred successfully');
      setTransferDialog(false);
      setTransferForm({ from_store_id: '', to_store_id: '', items: [{ item_id: '', quantity: 0 }] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to transfer stock');
    }
  };

  const getItemName = (itemId) => items.find(i => i.id === itemId)?.name || itemId;
  const getStoreName = (storeId) => stores.find(s => s.id === storeId)?.name || storeId;
  
  const getMovementIcon = (type) => {
    if (type?.includes('in') || type === 'return') return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (type?.includes('transfer')) return <ArrowRightLeft className="h-4 w-4 text-blue-500" />;
    return <TrendingDown className="h-4 w-4 text-red-500" />;
  };

  const getMovementColor = (type) => {
    if (type?.includes('in') || type === 'return') return 'bg-green-500';
    if (type?.includes('transfer')) return 'bg-blue-500';
    return 'bg-red-500';
  };

  const filteredMovements = movements.filter(m => {
    if (filterStore !== 'all' && m.store_id !== filterStore) return false;
    if (filterType !== 'all' && m.movement_type !== filterType) return false;
    return true;
  });

  const addTransferItem = () => {
    setTransferForm({
      ...transferForm,
      items: [...transferForm.items, { item_id: '', quantity: 0 }]
    });
  };

  const updateTransferItem = (idx, field, value) => {
    const items = [...transferForm.items];
    items[idx][field] = value;
    setTransferForm({ ...transferForm, items });
  };

  const removeTransferItem = (idx) => {
    if (transferForm.items.length > 1) {
      const items = transferForm.items.filter((_, i) => i !== idx);
      setTransferForm({ ...transferForm, items });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 rounded-full border-4 border-orange-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="stock-movements-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Movements</h1>
          <p className="text-gray-500">Track stock in, stock out, and transfers</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setTransferDialog(true)} variant="outline">
            <ArrowRightLeft className="h-4 w-4 mr-2" />Transfer Stock
          </Button>
          <Button onClick={() => setMovementDialog(true)} className="bg-orange-500 hover:bg-orange-600">
            <Plus className="h-4 w-4 mr-2" />Record Movement
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-500">Filters:</span>
            </div>
            <Select value={filterStore} onValueChange={setFilterStore}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Stores" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stores</SelectItem>
                {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="stock_in">Stock In</SelectItem>
                <SelectItem value="stock_out">Stock Out</SelectItem>
                <SelectItem value="transfer_in">Transfer In</SelectItem>
                <SelectItem value="transfer_out">Transfer Out</SelectItem>
                <SelectItem value="return">Return</SelectItem>
                <SelectItem value="adjustment">Adjustment</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMovements.map(m => (
                <TableRow key={m.id}>
                  <TableCell className="text-sm">{new Date(m.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="font-mono text-sm">{m.reference_number || '-'}</TableCell>
                  <TableCell>{getStoreName(m.store_id)}</TableCell>
                  <TableCell className="font-medium">{getItemName(m.item_id)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getMovementIcon(m.movement_type)}
                      <Badge className={getMovementColor(m.movement_type)}>
                        {m.movement_type?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{m.quantity}</TableCell>
                  <TableCell className="font-mono">₹{m.total_cost?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell className="text-sm text-gray-500 max-w-[200px] truncate">{m.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredMovements.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <History className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p>No stock movements found.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Movement Dialog */}
      <Dialog open={movementDialog} onOpenChange={setMovementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Stock Movement</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Store *</Label>
              <Select value={movementForm.store_id} onValueChange={(v) => setMovementForm({...movementForm, store_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>
                  {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Item *</Label>
              <Select value={movementForm.item_id} onValueChange={(v) => setMovementForm({...movementForm, item_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  {items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Movement Type</Label>
                <Select value={movementForm.movement_type} onValueChange={(v) => setMovementForm({...movementForm, movement_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stock_in">Stock In</SelectItem>
                    <SelectItem value="stock_out">Stock Out</SelectItem>
                    <SelectItem value="return">Return</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Quantity</Label>
                <Input 
                  type="number"
                  value={movementForm.quantity}
                  onChange={(e) => setMovementForm({...movementForm, quantity: Number(e.target.value)})}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Unit Cost (₹)</Label>
              <Input 
                type="number"
                step="0.01"
                value={movementForm.unit_cost}
                onChange={(e) => setMovementForm({...movementForm, unit_cost: Number(e.target.value)})}
              />
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Textarea 
                value={movementForm.notes}
                onChange={(e) => setMovementForm({...movementForm, notes: e.target.value})}
                placeholder="Reason for movement..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateMovement} className="bg-orange-500 hover:bg-orange-600">Record Movement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Transfer Stock Between Stores</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>From Store *</Label>
                <Select value={transferForm.from_store_id} onValueChange={(v) => setTransferForm({...transferForm, from_store_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>To Store *</Label>
                <Select value={transferForm.to_store_id} onValueChange={(v) => setTransferForm({...transferForm, to_store_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
                  <SelectContent>
                    {stores.filter(s => s.id !== transferForm.from_store_id).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Items to Transfer</Label>
              {transferForm.items.map((item, idx) => (
                <div key={idx} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Select value={item.item_id} onValueChange={(v) => updateTransferItem(idx, 'item_id', v)}>
                      <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
                      <SelectContent>
                        {items.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <Input 
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateTransferItem(idx, 'quantity', Number(e.target.value))}
                    />
                  </div>
                  {transferForm.items.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeTransferItem(idx)}>×</Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addTransferItem}>+ Add Item</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTransfer} className="bg-orange-500 hover:bg-orange-600">Transfer Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
